/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/camelcase */

import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import * as R from 'ramda';

import { PosPlugin } from 'plugins/plugin';
import { doClientRequest } from 'services/ErplyAPI/core/ErplyAPI';
import { getCustomers, getCustomerRewardPoints } from 'services/ErplyAPI';
import { getSalesDocuments } from 'services/ErplyAPI/sales';
import { IssuedCoupon } from 'types/Coupon';
import { getPayments } from 'reducers/Payments';
import { getProductsInShoppingCart } from 'reducers/ShoppingCart';
import * as jsonApi from 'services/JsonAPI';
import { addError } from 'actions/Error';
import { RootState } from 'reducers';
import {
  spreadCart,
  unspreadCart,
} from 'utils/apiTransformations/saveSalesDocumentCart';

import { saveWBUDataToInvoiceInJsonApi } from '../API/jsonAPI';
import { getCustomerWithExtraWbuData } from '../redux';
import WBUAPI from '../API/API';

import { GivexIncrementJson, PrintoutExtrasWBURecord } from './types';

export const getBBCouponsCount = (
  invoiceID,
): Promise<PrintoutExtrasWBURecord['issuedCouponsCount']> =>
  doClientRequest<IssuedCoupon>({
    request: 'getIssuedCoupons',
    issuedInvoiceIDs: invoiceID,
  })
    .then(
      coupons =>
        // comparison with constant 'BB' code requested by ticket Reporter
        // API only allows searching by customerID and timestamps, but in the same range there might be more than 1 sales generates,
        // so after we fetch all the earned points sales for that time frame, we need to find the correct doc by invoiceID
        coupons.filter(c => c.couponCode === 'BB').length,
    )
    .catch(err => {
      console.error('Failed getting issued coupons', err);
      // if we cannot fund any records better admit it instead of falsy assume that the amount is 0 - avoids confusion for the customers
      return undefined;
    });

export const getCustomerMembershipEndDate = (clientID: number) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
) => {
  const data = await dispatch(WBUAPI.getCustomerMemberships(clientID));
  return data.endDateTime;
};

export const getRewardPointsForSale = (
  invoiceID,
): Promise<PrintoutExtrasWBURecord['earnedRewardPoints']> =>
  doClientRequest<{ earnedPoints: number }>({
    request: 'getEarnedRewardPointRecords',
    invoiceID,
  })
    .then(data => data?.[0]?.earnedPoints ?? 0)
    .catch(err => {
      console.error('Failed getting earned reward points', err);
      // if we cannot fund any records better admit it instead of falsy assume that the amount is 0 - avoids confusion for the customers
      return undefined;
    });

export const saveReceiptDataToJSON: Required<
  PosPlugin
>['onSaveSalesDocument']['after'] = (p, o) => async (dispatch, getState) => {
  const savedSaleDocument = o.salesDocument;

  const salesDocument = await getSalesDocuments({
    id: savedSaleDocument.invoiceID,
    getAddedTimestamp: 1,
  }).then(data => data?.[0]);

  const [birdBuckCouponsTotal, earnedPoints, customer] = await Promise.all([
    getBBCouponsCount(savedSaleDocument.invoiceID),
    getRewardPointsForSale(savedSaleDocument.invoiceID),
    getCustomers({ customerID: salesDocument.clientID })
      .then(data => data?.[0])
      .catch(err => {
        console.error('Failed to load customer', salesDocument.clientID, err);
        return {};
      }),
  ]);
  const customerWithData = await dispatch(
    getCustomerWithExtraWbuData(String(salesDocument.clientID)),
  );

  const { endDateTime = '' } = customerWithData;
  const [{ points: rewardPoints = 0 }] = await getCustomerRewardPoints(
    customer.id,
  );

  const wbuData: PrintoutExtrasWBURecord = {
    dscExpirationDate: endDateTime ?? '',
    dscCurrentSavings: Number(rewardPoints),
    customerGroup: customer.groupID ?? 0,
    earnedRewardPoints: earnedPoints,
    issuedCouponsCount: birdBuckCouponsTotal,
  };

  await saveWBUDataToInvoiceInJsonApi(
    Number(savedSaleDocument.invoiceID),
    wbuData,
  );
};

type GivexJsonData = GivexIncrementJson & {
  rowNumber: number;
};

function addGivexDataToRowJdoc(
  existingRowJDoc: string | undefined,
  index: number,
  paymentData: GivexJsonData[],
): string | undefined {
  const rowPaymentData = paymentData.find(
    payment => payment.rowNumber - 1 === index,
  );
  if (!rowPaymentData) {
    return existingRowJDoc;
  }
  const { rowNumber, ...payload } = rowPaymentData;
  const newRowJDoc = {
    BrazilPOS: {
      givex: payload,
    },
  };
  if (!existingRowJDoc) {
    return JSON.stringify(
      R.mergeDeepRight(jsonApi.emptyJdocForType.inv_rows, newRowJDoc),
    );
  }
  let existingRowJDocObject;
  try {
    existingRowJDocObject = JSON.parse(existingRowJDoc);
  } catch (error) {
    existingRowJDocObject = {};
  }
  const mergedRowJDoc = R.reduce(
    R.mergeDeepWith((a, b) => b ?? a),
    jsonApi.emptyJdocForType.inv_rows,
    [existingRowJDocObject, newRowJDoc],
  );
  return JSON.stringify(mergedRowJDoc);
}

export const saveRowDataToJSON: Required<
  PosPlugin
>['onSaveSalesDocument']['on'] = (p, ap) => async (dispatch, getState) => {
  const payments = getPayments(getState());
  const cart = getProductsInShoppingCart(getState());

  try {
    const givexIncrementPayments: GivexJsonData[] = Object.values(payments)
      .filter((payment: any) => {
        const isGivexIncrement =
          payment.cardType === 'GIVEX' &&
          ['Increment', 'Activate'].includes(payment.transactionType) &&
          payment.GIVEX;
        // uuid is attached to rows that contain a Givex increment product
        const isReturnPayment = !cart.some(c => c.uuid === payment.key);
        return isGivexIncrement && !isReturnPayment;
      })
      .map((value: any) => ({
        number: value.GIVEX.card.cardNo.slice(-10, -1),
        balance: value.certificateBalance,
        referenceNumber: value.referenceNumber.slice(-6),
        dateTime: value.dateTime,
        rowNumber: cart.find(c => c.uuid === value.key).rowNumber,
      }));

    if (!givexIncrementPayments.length) {
      return ap;
    }

    return ap.map(request => {
      if (request.requestName !== 'saveSalesDocument') {
        return request;
      }
      const updatedPartialRows = unspreadCart(request)
        .cart.map(row => (row as typeof row & { rowJDoc?: string }).rowJDoc)
        .map((rowJDoc, i) =>
          addGivexDataToRowJdoc(rowJDoc, i, givexIncrementPayments),
        )
        // do not filter out empty rowJDocs to preserve initial order after the cart is spread
        .map(rowJDoc => ({ rowJDoc, promotionRules: [] }));

      return {
        ...request,
        ...spreadCart({ cart: updatedPartialRows }),
      };
    });
  } catch (e) {
    dispatch(addError('Failed to store GIVEX info in JSON API'));
    console.error('Failed to store GIVEX info in JSON API\n', e);
    return ap;
  }
};
